import { useLocale } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

interface LanguageSwitcherProps {
  className?: string;
  variant?: "default" | "compact";
}

function USFlag({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 640 480" className={className}>
      <g fillRule="evenodd">
        <g strokeWidth="1pt">
          <path fill="#bd3d44" d="M0 0h640v37H0zm0 73.9h640v37H0zm0 73.8h640v37H0zm0 73.8h640v37H0zm0 74h640v36.8H0zm0 73.7h640v37H0zm0 73.9h640v37H0z"/>
          <path fill="#fff" d="M0 37h640v36.9H0zm0 73.8h640v36.9H0zm0 73.8h640v37H0zm0 73.9h640v37H0zm0 73.8h640v37H0zm0 73.8h640v37H0z"/>
        </g>
        <path fill="#192f5d" d="M0 0h364.8v258.5H0z"/>
        <path fill="#fff" d="m30.4 11 3.4 10.3h10.6l-8.6 6.3 3.3 10.3-8.7-6.4-8.6 6.3 3.3-10.2-8.6-6.3h10.7zm60.8 0 3.3 10.3h10.8l-8.7 6.3 3.2 10.3-8.6-6.4-8.7 6.3 3.3-10.2-8.6-6.3h10.6zm60.8 0 3.3 10.3H166l-8.6 6.3 3.3 10.3-8.7-6.4-8.7 6.3 3.3-10.2-8.5-6.3h10.5zm60.8 0 3.3 10.3h10.8l-8.7 6.3 3.3 10.3-8.7-6.4-8.7 6.3 3.4-10.2-8.8-6.3h10.7zm60.8 0 3.3 10.3h10.7l-8.6 6.3 3.3 10.3-8.7-6.4-8.7 6.3 3.3-10.2-8.6-6.3h10.8zM60.8 37l3.3 10.2H75l-8.7 6.2 3.2 10.3-8.5-6.3-8.7 6.3 3.1-10.3-8.4-6.2h10.6zm60.8 0 3.4 10.2h10.7l-8.8 6.2 3.4 10.3-8.7-6.3-8.7 6.3 3.3-10.3-8.7-6.2h10.8zm60.8 0 3.3 10.2h10.8l-8.7 6.2 3.3 10.3-8.7-6.3-8.6 6.3 3.2-10.3-8.6-6.2H176zm60.8 0 3.4 10.2h10.7l-8.8 6.2 3.4 10.3-8.7-6.3-8.6 6.3 3.2-10.3-8.5-6.2h10.6z"/>
      </g>
    </svg>
  );
}

function MXFlag({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 640 480" className={className}>
      <path fill="#006847" d="M0 0h213.3v480H0z"/>
      <path fill="#fff" d="M213.3 0h213.4v480H213.3z"/>
      <path fill="#ce1126" d="M426.7 0H640v480H426.7z"/>
    </svg>
  );
}

export function LanguageSwitcher({ className, variant = "default" }: LanguageSwitcherProps) {
  const { locale, switchLanguage } = useLocale();

  return (
    <button
      onClick={switchLanguage}
      className={cn(
        "inline-flex items-center gap-2 font-sans text-sm font-medium transition-all",
        "text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-md",
        variant === "compact" ? "px-2 py-1" : "px-3 py-1.5 border border-border rounded-md",
        className
      )}
      aria-label={locale === "en" ? "EN ES – Cambiar a español" : "EN ES – Switch to English"}
    >
      <span className={cn(
        "inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded transition-all",
        locale === "en"
          ? "bg-primary/15 text-foreground font-bold"
          : "text-muted-foreground"
      )}>
        <USFlag className="h-3.5 w-5 rounded-sm overflow-hidden shrink-0" />
        EN
      </span>
      <span className="text-border/60">|</span>
      <span className={cn(
        "inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded transition-all",
        locale === "es"
          ? "bg-primary/15 text-foreground font-bold"
          : "text-muted-foreground"
      )}>
        <MXFlag className="h-3.5 w-5 rounded-sm overflow-hidden shrink-0" />
        ES
      </span>
    </button>
  );
}
